/*
 * PEGASI cart — self-contained AJAX cart for the drop shell.
 *
 * Horizon's <cart-drawer> lives in header-group, which is suppressed on
 * index/collection/product. Rather than couple to it, this owns the whole
 * add -> drawer -> badge path with no dependencies:
 *   - intercepts the purchase-gate product form (form.pg-pdp__form)
 *   - POSTs to /cart/add.js, then re-reads /cart.js and re-renders
 *   - opens a focus-trapped drawer and updates every cart badge
 *   - line quantity +/- and remove via /cart/change.js
 *
 * Progressive enhancement: cart icons keep their href to /cart, so if this
 * script fails to load the store is still fully usable.
 */
(() => {
  const routes = (window.Theme && window.Theme.routes) || {};
  const ADD_URL = routes.cart_add_url || '/cart/add.js';
  const CHANGE_URL = (routes.cart_change_url || '/cart/change') + '.js';
  const CART_JS = '/cart.js';

  const state = { open: false, busy: false, lastFocus: null };
  let drawer = null;
  let panel = null;
  let linesEl = null;
  let subtotalEl = null;
  let liveEl = null;

  // Copy the drawer carries as data-pg-* attributes (Theme settings -> Pegasi cart drawer).
  const label = (key, fallback) => (drawer && drawer.dataset[key]) || fallback;

  const money = (cents, currency) => {
    const value = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'USD',
      }).format(value);
    } catch (err) {
      return '$' + value.toFixed(2);
    }
  };

  const sizedImage = (url) => {
    if (!url) return '';
    // Shopify's image CDN honours ?width= and negotiates WebP/AVIF.
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=160';
  };

  const escapeHtml = (str) =>
    String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const setBusy = (busy) => {
    state.busy = busy;
    if (drawer) drawer.classList.toggle('is-busy', busy);
  };

  const updateBadges = (count) => {
    document.querySelectorAll('[data-pg-cart-count]').forEach((el) => {
      el.textContent = count;
      // Pills opt into hiding at 0 via data-hide-empty; inline "Cart [0]" counts stay.
      if (el.hasAttribute('data-hide-empty')) el.hidden = count < 1;
    });
    document.querySelectorAll('[data-pg-cart-open]').forEach((el) => {
      const base = (el.getAttribute('data-cart-label') || 'Cart').trim();
      el.setAttribute(
        'aria-label',
        count > 0 ? `${base}, ${count} ${count === 1 ? label('pgItemSingular', 'item') : label('pgItemPlural', 'items')}` : base
      );
    });
  };

  const renderLines = (cart) => {
    updateBadges(cart.item_count);
    if (!drawer) return;
    drawer.classList.toggle('is-empty', cart.item_count === 0);
    if (subtotalEl) {
      const sub = cart.items_subtotal_price != null ? cart.items_subtotal_price : cart.total_price;
      subtotalEl.textContent = money(sub, cart.currency);
    }
    if (!linesEl) return;

    // Same label the server-rendered lines carry (Theme settings -> Pegasi cart drawer).
    const removeLabel = escapeHtml(label('pgRemoveLabel', 'Remove'));
    linesEl.innerHTML = cart.items
      .map((item) => {
        const img = sizedImage(item.image);
        const showVariant = !item.product_has_only_default_variant && item.variant_title;
        return (
          `<li class="pg-cart__line" data-pg-line data-key="${escapeHtml(item.key)}">` +
          `<a class="pg-cart__thumb" href="${escapeHtml(item.url)}" tabindex="-1">` +
          (img ? `<img src="${escapeHtml(img)}" alt="" width="80" height="80" loading="lazy">` : '') +
          `</a>` +
          `<div class="pg-cart__info">` +
          `<a class="pg-cart__title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>` +
          (showVariant ? `<p class="pg-cart__variant">${escapeHtml(item.variant_title)}</p>` : '') +
          `<div class="pg-cart__controls">` +
          `<div class="pg-cart__qty" data-pg-qty>` +
          // Icons come from the CSS mask on .pg-ico (Iconsax straight outline),
          // so no glyphs or asset URLs need threading through JS.
          `<button class="pg-ico pg-ico--minus" type="button" data-pg-qty-dec aria-label="${escapeHtml(label('pgQtyDecLabel', 'Decrease quantity'))}"></button>` +
          `<span data-pg-qty-value aria-live="polite">${item.quantity}</span>` +
          `<button class="pg-ico pg-ico--add" type="button" data-pg-qty-inc aria-label="${escapeHtml(label('pgQtyIncLabel', 'Increase quantity'))}"></button>` +
          `</div>` +
          `<button type="button" class="pg-cart__remove" data-pg-remove aria-label="${removeLabel} ${escapeHtml(item.product_title)}">${removeLabel}</button>` +
          `</div>` +
          `</div>` +
          `<span class="pg-cart__price">${money(item.final_line_price, cart.currency)}</span>` +
          `</li>`
        );
      })
      .join('');
  };

  const getCart = () =>
    fetch(CART_JS, { headers: { Accept: 'application/json' } }).then((r) => r.json());

  const refresh = () => getCart().then((cart) => { renderLines(cart); return cart; });

  const focusable = () =>
    Array.from(
      drawer.querySelectorAll(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null);

  const onKeydown = (event) => {
    if (event.key === 'Escape') {
      closeDrawer();
      return;
    }
    if (event.key !== 'Tab') return;
    const list = focusable();
    if (!list.length) return;
    const first = list[0];
    const last = list[list.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const openDrawer = () => {
    if (!drawer || state.open) return;
    state.lastFocus = document.activeElement;
    state.open = true;
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    // next frame so the transition runs from the hidden state
    requestAnimationFrame(() => drawer.classList.add('is-open'));
    document.documentElement.classList.add('pg-cart-locked');
    document.addEventListener('keydown', onKeydown);
    const close = drawer.querySelector('[data-pg-cart-close]');
    if (close) close.focus();
  };

  const closeDrawer = () => {
    if (!drawer || !state.open) return;
    state.open = false;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('pg-cart-locked');
    document.removeEventListener('keydown', onKeydown);
    const finish = () => {
      if (!state.open) drawer.hidden = true;
      panel.removeEventListener('transitionend', finish);
    };
    if (panel) panel.addEventListener('transitionend', finish);
    else drawer.hidden = true;
    if (state.lastFocus && typeof state.lastFocus.focus === 'function') state.lastFocus.focus();
  };

  const announce = (msg) => {
    if (!liveEl) return;
    liveEl.textContent = '';
    requestAnimationFrame(() => { liveEl.textContent = msg; });
  };

  const showError = (form, msg) => {
    let el = form.querySelector('[data-pg-cart-error]');
    if (!el) {
      el = document.createElement('p');
      el.className = 'pg-gate__error pg-cart__form-error';
      el.setAttribute('role', 'alert');
      el.setAttribute('data-pg-cart-error', '');
      form.appendChild(el);
    }
    el.textContent = msg || label('pgErrorGeneric', 'Something went wrong. Please try again.');
  };

  const clearError = (form) => {
    const el = form.querySelector('[data-pg-cart-error]');
    if (el) el.textContent = '';
  };

  const addToCart = (form) => {
    if (state.busy) return;
    const btn = form.querySelector('[data-pg-submit], button[type="submit"], [name="add"]');
    const labelEl = form.querySelector('[data-pg-submit-label]');
    const prevLabel = labelEl ? labelEl.textContent : null;
    clearError(form);
    setBusy(true);
    form.classList.add('is-loading');
    if (btn) btn.setAttribute('aria-busy', 'true');
    if (labelEl) labelEl.textContent = label('pgAddingLabel', 'Adding…');

    fetch(ADD_URL, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: new FormData(form),
    })
      .then((res) =>
        res.ok
          ? res.json()
          : res.json().then(
              (err) => Promise.reject(new Error(err.description || err.message || label('pgAddError', 'Could not add to cart.'))),
              () => Promise.reject(new Error(label('pgAddError', 'Could not add to cart.')))
            )
      )
      .then(() => refresh())
      .then(() => {
        openDrawer();
        announce(label('pgAddedAnnounce', 'Added to cart.'));
        if (labelEl) {
          labelEl.textContent = label('pgAddedLabel', 'Added ✓');
          setTimeout(() => { if (labelEl) labelEl.textContent = prevLabel; }, 1600);
        }
      })
      .catch((err) => {
        showError(form, err && err.message);
        if (labelEl) labelEl.textContent = prevLabel;
      })
      .finally(() => {
        setBusy(false);
        form.classList.remove('is-loading');
        if (btn) btn.removeAttribute('aria-busy');
      });
  };

  const changeLine = (key, quantity) => {
    if (state.busy) return;
    setBusy(true);
    fetch(CHANGE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    })
      .then((res) => res.json())
      .then((cart) => { renderLines(cart); })
      .catch(() => {})
      .finally(() => setBusy(false));
  };

  const onClick = (event) => {
    const opener = event.target.closest('[data-pg-cart-open]');
    if (opener) {
      // If the drawer isn't on this template, let the link navigate to /cart
      // instead of swallowing the click and leaving a dead cart icon.
      if (!drawer) return;
      event.preventDefault();
      openDrawer();
      refresh().catch(() => {});
      return;
    }
    if (event.target.closest('[data-pg-cart-close]') || event.target.closest('[data-pg-cart-overlay]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }
    const line = event.target.closest('[data-pg-line]');
    if (!line || !drawer || !drawer.contains(line)) return;
    const key = line.getAttribute('data-key');
    const valueEl = line.querySelector('[data-pg-qty-value]');
    const current = parseInt(valueEl ? valueEl.textContent : '1', 10) || 1;
    if (event.target.closest('[data-pg-qty-inc]')) changeLine(key, current + 1);
    else if (event.target.closest('[data-pg-qty-dec]')) changeLine(key, Math.max(0, current - 1));
    else if (event.target.closest('[data-pg-remove]')) changeLine(key, 0);
  };

  const onSubmit = (event) => {
    // The purchase gate renders the add-to-cart form with class pg-pdp__form
    // (Shopify's {% form 'product' %}). Match on that — a class is guaranteed to
    // render, where a hyphenated data-* on the {% form %} tag is not.
    const form = event.target.closest('form.pg-pdp__form');
    if (!form) return;
    event.preventDefault();
    addToCart(form);
  };

  const bind = () => {
    drawer = document.querySelector('[data-pg-cart]');
    if (drawer) {
      panel = drawer.querySelector('[data-pg-cart-panel]');
      linesEl = drawer.querySelector('[data-pg-cart-lines]');
      subtotalEl = drawer.querySelector('[data-pg-cart-subtotal]');
      liveEl = drawer.querySelector('[data-pg-cart-live]');
    }
    document.addEventListener('click', onClick);
    document.addEventListener('submit', onSubmit);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind);
  else bind();
})();
